import { Card, Icon, Tag, Text, Title } from '@nimbus-ds/components'
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowsHorizontalIcon,
  ArrowDownIcon,
} from '@nimbus-ds/icons'
import { cn } from '@/lib/utils'
import { boundedDownsideCallOf, decidingMetricOf } from '@/lib/economic-calculations'
import type { Experiment, ExperimentResults } from '@/types'

/**
 * RECOMENDACIÓN — merchant layer, RESULT-first structure:
 *   1. Title  = the OUTCOME (Confirmado / Descartado / Sin efecto / …) — follows the HYPOTHESIS, so a
 *      negative test where the tested element proves its worth reads as Confirmado, in green.
 *   2. Action = what to DO, in physical vocabulary around "el cambio" (= the treatment modification).
 *      Polarity sets the verbs: on a NORMAL test the change is new → aplicar / no aplicar; on a
 *      NEGATIVE test the change is ALREADY LIVE in the store → nada que hacer / revertir.
 *
 * Green is earned only by a confirmed hypothesis or a safe apply — never by which arm won. No-loss
 * calls (posterior under the bar) never carry Confirmado/Descartado: they are calls, not verdicts.
 * Copy is a deterministic state machine over the payload (facts-vs-rules): no free-form judgment.
 */

const MIN_SAMPLE = 100

interface ArmC {
  sessions: number
  conversions: number
}

const DEVICE_LABEL: Record<string, string> = { mobile: 'Mobile', desktop: 'Desktop' }

type Tone = 'success' | 'warning' | 'info' | 'neutral'

const TONE_CLASS: Record<Tone, string> = {
  success: 'border-l-4 border-l-green-500 bg-green-50/40',
  warning: 'border-l-4 border-l-amber-500 bg-amber-50/40',
  info: 'border-l-4 border-l-blue-500 bg-blue-50/30',
  neutral: '',
}

interface RecommendationBannerProps {
  experiment: Experiment | null
  results: ExperimentResults | null
}

export function RecommendationBanner({ experiment, results }: RecommendationBannerProps) {
  const r = results as any
  const sessions = r?.sessions as Record<string, number> | undefined
  const hasData = Boolean(r && sessions && r.conversion_rate)
  const minSampleReached =
    hasData && (sessions!.control || 0) >= MIN_SAMPLE && (sessions!.treatment || 0) >= MIN_SAMPLE

  const isNegative = Boolean(experiment?.is_negative || r?.is_negative)
  const eta = r?.decision_eta
  const summary = r?.summary
  const winner: string | null = summary?.is_significant && summary?.winner ? summary.winner : null
  const vetoed = eta?.state === 'vetoed' || r?.guardrail?.state === 'veto'
  const deciding = decidingMetricOf(r)
  const primaryIsPurchase = summary?.primary_metric?.kind === 'purchase'
  const metricIsPurchase = deciding === 'purchase' || primaryIsPurchase
  const noun = metricIsPurchase ? 'ventas' : summary?.primary_metric?.label || 'el objetivo'

  // Concrete name for the change when the treatment variant carries a short, human description.
  const changeDesc = (() => {
    const entry = Object.entries(experiment?.variants ?? {}).find(([key]) => key.toLowerCase() !== 'control')
    const d = (entry?.[1]?.description || '').trim()
    return d.length >= 3 && d.length <= 48 ? d : null
  })()
  const cambio = changeDesc ? `el cambio (${changeDesc.toLowerCase()})` : 'el cambio'

  // Counts of the DECIDING metric per arm. Labels are polarity-aware: on a normal test the store
  // as-is = control and the change = treatment; on a negative test the change is already live
  // (control) and treatment is the reverted version.
  const counts = ((): { control: ArmC; treatment: ArmC } | null => {
    if (!hasData) return null
    if (deciding === 'purchase' && r.guardrail?.counts) {
      return { control: r.guardrail.counts.control, treatment: r.guardrail.counts.treatment }
    }
    return {
      control: { sessions: sessions!.control || 0, conversions: r.conversions?.control || 0 },
      treatment: { sessions: sessions!.treatment || 0, conversions: r.conversions?.treatment || 0 },
    }
  })()

  const fmtArm = (c: ArmC) => {
    const rate = c.sessions > 0 ? ((c.conversions / c.sessions) * 100).toFixed(1) : '0.0'
    return `${c.conversions.toLocaleString('es-ES')} ${noun} en ${c.sessions.toLocaleString('es-ES')} visitas (${rate}%)`
  }
  const dataLine = counts
    ? isNegative
      ? `Con el cambio: ${fmtArm(counts.control)} · Si se revierte: ${fmtArm(counts.treatment)}`
      : `Como está: ${fmtArm(counts.control)} · Con el cambio: ${fmtArm(counts.treatment)}`
    : null

  // How big is the difference — only as precise as the estimate allows (merchant-honest).
  const magnitudeLine = ((): string | null => {
    if (!winner || !counts) return null
    const precision =
      deciding === 'purchase'
        ? r.guardrail?.lift?.precision?.level
        : summary?.lift?.precision?.level
    const w = winner === 'control' ? counts.control : counts.treatment
    const l = winner === 'control' ? counts.treatment : counts.control
    const wRate = w.sessions > 0 ? w.conversions / w.sessions : 0
    const lRate = l.sessions > 0 ? l.conversions / l.sessions : 0
    if (lRate <= 0 || wRate <= 0) return null
    if (precision === 'precise' || precision === 'approximate') {
      const rel = ((wRate - lRate) / lRate) * 100
      return `La opción ganadora logra ~${Math.abs(rel).toFixed(0)}% más ${noun}.`
    }
    return `Cuánto más ${metricIsPurchase ? 'vende' : 'logra'} todavía es impreciso: puede ser poco o mucho, pero la dirección es clara.`
  })()

  // Device note — only when the per-device cells measure the same metric that decided.
  const deviceLine = ((): string | null => {
    if (!winner) return null
    const bd = r?.by_device
    if (!bd?.present || !bd.segments) return null
    const bdMetric = bd.metric ?? 'purchase'
    if (bdMetric !== (metricIsPurchase ? 'purchase' : 'goal')) return null
    const winnerVerdict = winner === 'treatment' ? 'treatment_better' : 'control_better'
    const keys = (['mobile', 'desktop'] as const).filter((k) => bd.segments[k])
    if (keys.length < 2) return null
    const verdictOf = (k: string) => bd.segments[k]?.bb_rope?.verdict ?? 'inconclusive'
    const against = keys.find((k) => verdictOf(k) !== 'inconclusive' && verdictOf(k) !== winnerVerdict)
    if (against) return `Ojo: en ${DEVICE_LABEL[against]} el resultado apunta al revés — está detallado en Números.`
    const agree = keys.filter((k) => verdictOf(k) === winnerVerdict)
    if (agree.length === keys.length) return 'El resultado se repite en Mobile y Desktop.'
    if (agree.length === 1) return `El resultado viene sobre todo de ${DEVICE_LABEL[agree[0]]}.`
    return null
  })()

  // ---- the state machine: { tag, tone, icon, title = RESULT, action = WHAT TO DO } ----
  const view = (() => {
    if (!hasData || !minSampleReached) {
      return {
        tone: 'neutral' as Tone,
        tag: { label: 'Juntando datos', appearance: 'neutral' as const },
        icon: <ClockIcon />,
        iconClass: 'text-gray-500',
        title: 'Todavía juntando datos.',
        action: 'Dejá la prueba correr: aún no hay visitas suficientes para leer un resultado.',
      }
    }
    if (vetoed) {
      return {
        tone: 'warning' as Tone,
        tag: { label: 'Contradictorio', appearance: 'warning' as const },
        icon: <ExclamationTriangleIcon />,
        iconClass: 'text-amber-600',
        title: `El cambio mejora ${metricIsPurchase ? 'el objetivo' : noun} pero vende menos.`,
        action: 'No decidas todavía: revisalo con tu consultor.',
      }
    }
    if (winner) {
      const hypothesisWon = (winner === 'treatment') !== isNegative
      if (isNegative) {
        // The change is already live: keep (nothing to do) or revert.
        return hypothesisWon
          ? {
              tone: 'success' as Tone,
              tag: { label: 'Confirmado', appearance: 'success' as const },
              icon: <CheckCircleIcon />,
              iconClass: 'text-green-600',
              title: 'Confirmado: el cambio sí aporta.',
              action: 'Nada que hacer — el cambio ya está aplicado. Cerrá la prueba.',
            }
          : {
              tone: 'warning' as Tone,
              tag: { label: 'Descartado', appearance: 'warning' as const },
              icon: <ArrowDownIcon />,
              iconClass: 'text-amber-600',
              title: 'Descartado: el cambio no está aportando.',
              action: `Revertí ${cambio} y cerrá la prueba.`,
            }
      }
      // Normal test: the change is new — apply it or don't.
      return hypothesisWon
        ? {
            tone: 'success' as Tone,
            tag: { label: 'Confirmado', appearance: 'success' as const },
            icon: <CheckCircleIcon />,
            iconClass: 'text-green-600',
            title: metricIsPurchase
              ? 'Funcionó: el cambio genera más ventas.'
              : `Funcionó: el cambio mejora ${noun}.`,
            action: `Aplicá ${cambio} y cerrá la prueba.`,
          }
        : {
            tone: 'warning' as Tone,
            tag: { label: 'Descartado', appearance: 'warning' as const },
            icon: <ArrowDownIcon />,
            iconClass: 'text-amber-600',
            title: metricIsPurchase
              ? 'No funcionó: el cambio no ayudó a vender más.'
              : `No funcionó: el cambio no mejoró ${noun}.`,
            action: 'No lo apliques y cerrá la prueba. Descartar una idea también es un resultado.',
          }
    }
    // CI asymmetry: not decided at the bar, but the leader's worst plausible case is immaterial →
    // acting carries essentially no risk. Never wears Confirmado/Descartado (a call, not a verdict).
    const bounded = boundedDownsideCallOf(r)
    if (bounded) {
      if (bounded.winnerArm === 'treatment') {
        return isNegative
          ? {
              tone: 'success' as Tone,
              tag: { label: 'Riesgo mínimo', appearance: 'success' as const },
              icon: <CheckCircleIcon />,
              iconClass: 'text-green-600',
              title: 'Revertirlo no genera pérdidas; cuánto mejoraría todavía es incierto.',
              action: `Podés revertir ${cambio} sin esperar.`,
            }
          : {
              tone: 'success' as Tone,
              tag: { label: 'Riesgo mínimo', appearance: 'success' as const },
              icon: <CheckCircleIcon />,
              iconClass: 'text-green-600',
              title: 'El cambio no genera pérdidas; cuánto mejora todavía es incierto.',
              action: `Podés aplicar ${cambio} sin esperar. Si necesitás medir cuánto mejora, dejá la prueba correr.`,
            }
      }
      return isNegative
        ? {
            tone: 'info' as Tone,
            tag: { label: 'Se puede cerrar', appearance: 'primary' as const },
            icon: <ArrowsHorizontalIcon />,
            iconClass: 'text-blue-600',
            title: 'Sin señales de que convenga revertirlo.',
            action: 'Podés cerrar: el cambio queda aplicado.',
          }
        : {
            tone: 'info' as Tone,
            tag: { label: 'Se puede cerrar', appearance: 'primary' as const },
            icon: <ArrowsHorizontalIcon />,
            iconClass: 'text-blue-600',
            title: 'Sin señales de que el cambio ayude.',
            action: 'Cerrá la prueba y reformulala: probá otra versión del cambio o una hipótesis distinta.',
          }
    }
    if (eta?.state === 'no_difference') {
      return {
        tone: 'info' as Tone,
        tag: { label: 'Sin efecto', appearance: 'primary' as const },
        icon: <ArrowsHorizontalIcon />,
        iconClass: 'text-blue-600',
        title: 'Sin efecto, confirmado: la prueba midió bien y no hay diferencia.',
        action: 'Cerrá y quedate con la opción más simple.',
      }
    }
    if (eta?.state === 'around_margin') {
      return {
        tone: 'info' as Tone,
        tag: { label: 'Efecto no medible', appearance: 'primary' as const },
        icon: <ArrowsHorizontalIcon />,
        iconClass: 'text-blue-600',
        title: 'El efecto, si existe, es más chico de lo que esta prueba puede medir.',
        action: 'Cerrá — o reformulá la prueba (más tráfico, margen más chico) si un efecto así te importa.',
      }
    }
    const days = eta?.state === 'wait' && eta.days_estimated != null ? Math.max(1, Math.ceil(eta.days_estimated)) : null
    return {
      tone: 'neutral' as Tone,
      tag: { label: 'En curso', appearance: 'primary' as const },
      icon: <ClockIcon />,
      iconClass: 'text-gray-500',
      title: 'Todavía sin resultado.',
      action: days
        ? `Dejá la prueba correr ~${days} día${days === 1 ? '' : 's'} más y volvé a mirar.`
        : 'Dejá la prueba correr y volvé a mirar en unos días.',
    }
  })()

  return (
    <Card className={cn(TONE_CLASS[view.tone])}>
      <Card.Body>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className={cn('mt-1 shrink-0', view.iconClass)}>
              <Icon source={view.icon} color="currentColor" />
            </span>
            <div className="min-w-0 space-y-1.5">
              <Title as="h2">{view.title}</Title>
              <Text fontWeight="bold" color="neutral-textHigh">
                → {view.action}
              </Text>
              {dataLine && (
                <Text fontSize="caption" color="neutral-textLow">
                  {dataLine}
                </Text>
              )}
              {magnitudeLine && <Text fontSize="caption">{magnitudeLine}</Text>}
              {deviceLine && <Text fontSize="caption">{deviceLine}</Text>}
            </div>
          </div>
          <div className="shrink-0">
            <Tag appearance={view.tag.appearance}>{view.tag.label}</Tag>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
